import { ActionTree } from 'vuex'
import RootState from '@vue-storefront/core/types/RootState'
import config from 'config'
import { adjustMultistoreApiUrl } from '@vue-storefront/core/lib/multistore'
import * as userTypes from '@vue-storefront/core/modules/user/store/mutation-types'
import { SN_USER } from '@vue-storefront/core/modules/user/store/mutation-types'
import { StorageManager } from '@vue-storefront/core/lib/storage-manager'
import { Logger } from '@vue-storefront/core/lib/logger'
import { USER_REFRESH_TOKEN_CLEARED } from './mutation-types'

const EXTENSION_PATH = '/api/ext/login-code'
const HTTP_OK = 200

const ERROR_REQUEST_FAILED = 'LOGIN_CODE_REQUEST_FAILED'
const ERROR_UNAVAILABLE = 'LOGIN_CODE_UNAVAILABLE'
const ERROR_INVALID = 'LOGIN_CODE_INVALID'
const ERROR_RATE_LIMITED = 'LOGIN_CODE_RATE_LIMITED'
const ERROR_ACCOUNT_BLOCKED = 'LOGIN_CODE_ACCOUNT_BLOCKED'

const KNOWN_ERRORS = [
  ERROR_REQUEST_FAILED,
  ERROR_UNAVAILABLE,
  ERROR_INVALID,
  ERROR_RATE_LIMITED,
  ERROR_ACCOUNT_BLOCKED
]

const HTTP_TOO_MANY_REQUESTS = 429

const post = (path: string, body: object): Promise<Response> =>
  fetch(adjustMultistoreApiUrl(`${config.api['url']}${EXTENSION_PATH}${path}`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  })

const failure = (code: string): Error => {
  const error = new Error(KNOWN_ERRORS.includes(code) ? code : ERROR_UNAVAILABLE)

  return error
}

const resultOf = async (response: Response, fallback: string): Promise<string> => {
  try {
    const payload = await response.json()

    return String(payload.result)
  } catch (parseError) {
    return fallback
  }
}

export const actions: ActionTree<any, RootState> = {
  async requestCode (context, { email }) {
    let response

    try {
      response = await post('/request', { email })
    } catch (networkError) {
      throw failure(ERROR_REQUEST_FAILED)
    }

    if (response.status === HTTP_TOO_MANY_REQUESTS) {
      throw failure(ERROR_RATE_LIMITED)
    }

    if (!response.ok) {
      throw failure(await resultOf(response, ERROR_REQUEST_FAILED))
    }

    return true
  },

  async verifyCode ({ commit, dispatch }, { email, code }) {
    let payload

    try {
      const response = await post('/verify', { email, code })
      payload = await response.json()
    } catch (networkError) {
      throw failure(ERROR_UNAVAILABLE)
    }

    if (payload.code !== HTTP_OK) {
      throw failure(String(payload.result))
    }

    await dispatch('resetUserInvalidateLock', {}, { root: true })

    commit(`${SN_USER}/${USER_REFRESH_TOKEN_CLEARED}`, null, { root: true })
    commit(`${SN_USER}/${userTypes.USER_TOKEN_CHANGED}`, { newToken: payload.result }, { root: true })
    await StorageManager.get('user').removeItem('current-refresh-token')

    try {
      await dispatch('user/me', { refresh: true, useCache: false }, { root: true })
    } catch (profileError) {
      await dispatch('user/clearCurrentUser', {}, { root: true })
      throw failure(ERROR_UNAVAILABLE)
    }

    try {
      await dispatch('user/getOrdersHistory', { refresh: true, useCache: false }, { root: true })
    } catch (ordersError) {
      Logger.error(ordersError, 'user')()
    }

    return payload.result
  }
}
